import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Phone, Briefcase, FileText, Loader2, ExternalLink, Clock, CheckCircle2, UserCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = [
  "Open", "Designing", "Printing", "Post Printing", "Drying",
  "Ready to Dispatch", "Out For Delivery", "Delivered",
  "Cancelled by Customer", "Cancelled by Staff"
];

const STAGE_ROLE_MAP = {
  Designing: "Designer",
  Printing: "Printing Technician",
  "Ready to Dispatch": "Runner",
  "Out For Delivery": "Delivery Staff",
};

const statusColors = {
  Open: { bg: "bg-slate-50", text: "text-slate-700", dot: "bg-slate-500" },
  Designing: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  Printing: { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500" },
  "Post Printing": { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-500" },
  Drying: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  "Ready to Dispatch": { bg: "bg-cyan-50", text: "text-cyan-700", dot: "bg-cyan-500" },
  "Out For Delivery": { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" },
  Delivered: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  "Cancelled by Customer": { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-500" },
  "Cancelled by Staff": { bg: "bg-red-50", text: "text-red-600", dot: "bg-red-500" },
  "Order Created": { bg: "bg-gray-50", text: "text-gray-700", dot: "bg-gray-500" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [staffModal, setStaffModal] = useState(null); // { status, role }
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [loadingStaff, setLoadingStaff] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}`, { headers: getAuthHeaders(), withCredentials: true });
      setOrder(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleStatusClick = async (newStatus) => {
    if (STAGE_ROLE_MAP[newStatus]) {
      // Need to select staff first
      setStaffModal({ status: newStatus, role: STAGE_ROLE_MAP[newStatus] });
      setSelectedStaff("");
      setLoadingStaff(true);
      try {
        const { data } = await axios.get(`${API}/staff/by-role/${STAGE_ROLE_MAP[newStatus]}`, { headers: getAuthHeaders(), withCredentials: true });
        setStaffList(data);
      } catch (err) { console.error(err); setStaffList([]); }
      finally { setLoadingStaff(false); }
    } else {
      // Direct status update
      setUpdating(true);
      try {
        const { data } = await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { headers: getAuthHeaders(), withCredentials: true });
        setOrder(data);
      } catch (err) { console.error(err); }
      finally { setUpdating(false); }
    }
  };

  const confirmStaffAssignment = async () => {
    if (!staffModal || !selectedStaff) return;
    const staff = staffList.find(s => s.id === selectedStaff);
    if (!staff) return;
    setUpdating(true);
    try {
      const { data } = await axios.put(`${API}/orders/${orderId}/status`, {
        status: staffModal.status,
        assigned_to_id: staff.id,
        assigned_to_name: staff.name,
      }, { headers: getAuthHeaders(), withCredentials: true });
      setOrder(data);
      setStaffModal(null);
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F3EF] flex items-center justify-center"><Loader2 className="animate-spin text-[#161616]" size={32} /></div>;
  if (!order) return <div className="min-h-screen bg-[#F4F3EF] flex items-center justify-center"><p className="text-[#5A5955]">Order not found.</p></div>;

  const sc = statusColors[order.status] || statusColors.Designing;
  const isCancelled = order.status.startsWith("Cancelled");
  const isDelivered = order.status === "Delivered";
  const assignments = order.assignments || {};

  return (
    <div data-testid="order-detail-page" className="min-h-screen bg-[#F4F3EF]">
      {/* Header */}
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/admin/orders")} data-testid="back-to-orders" className="p-2 hover:bg-[#F4F3EF] transition-colors">
            <ArrowLeft size={20} className="text-[#161616]" />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Order: {order.customer_name}</h1>
            <span className={`inline-block mt-1 px-3 py-0.5 text-xs font-bold ${sc.bg} ${sc.text}`}>{order.status}</span>
          </div>
        </div>
        {order.lead_id && (
          <Link to={`/admin/leads/${order.lead_id}`} data-testid="link-to-lead" className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold text-[#161616] border border-[#D9D7D0] hover:border-[#161616] transition-colors">
            <ExternalLink size={12} /> View Lead
          </Link>
        )}
      </div>

      <div className="px-8 py-8 space-y-8 max-w-5xl">
        {/* Status Update */}
        {!isCancelled && !isDelivered && (
          <div className="bg-white border border-[#D9D7D0] p-6" data-testid="order-status-update">
            <p className="text-xs font-bold text-[#5A5955] mb-4">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {ORDER_STATUSES.map((s) => {
                const isCurrent = s === order.status;
                const sConf = statusColors[s] || statusColors.Designing;
                const needsStaff = !!STAGE_ROLE_MAP[s];
                return (
                  <button key={s} onClick={() => handleStatusClick(s)} disabled={updating || isCurrent}
                    data-testid={`order-status-btn-${s.toLowerCase().replace(/\s/g, "-")}`}
                    className={`px-3 py-2 text-xs font-bold border transition-all ${
                      isCurrent ? `${sConf.bg} ${sConf.text} border-current` : "bg-white text-[#5A5955] border-[#D9D7D0] hover:border-[#161616] hover:bg-[#F7F6F2]"
                    } disabled:opacity-50`}>
                    {isCurrent && <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" />}
                    {needsStaff && !isCurrent && <UserCheck size={10} className="inline mr-1 -mt-0.5" />}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Stage Assignments */}
        {Object.keys(assignments).length > 0 && (
          <div className="bg-white border border-[#D9D7D0] p-6" data-testid="stage-assignments">
            <p className="text-xs font-bold text-[#5A5955] mb-4">Stage Assignments</p>
            <div className="flex flex-wrap gap-4">
              {Object.entries(assignments).map(([stage, staff]) => {
                const sConf = statusColors[stage] || statusColors.Designing;
                return (
                  <div key={stage} className={`flex items-center gap-2 px-4 py-2 border ${sConf.bg}`}>
                    <span className={`text-xs font-bold ${sConf.text}`}>{stage}:</span>
                    <span className="text-sm font-medium text-[#161616]">{staff.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Activity Timeline & Quotation */}
          <div className="lg:col-span-2 space-y-6">
            {/* Activity Timeline */}
            <div className="bg-white border border-[#D9D7D0] p-6" data-testid="activity-timeline">
              <p className="text-xs font-bold text-[#5A5955] mb-6">Activity Timeline</p>
              <div className="space-y-0">
                {[...(order.activity || [])].reverse().map((entry, i) => {
                  const entryColor = statusColors[entry.status] || statusColors["Order Created"];
                  const isFirst = i === 0;
                  const isLast = i === (order.activity?.length || 0) - 1;
                  return (
                    <div key={i} className="flex gap-4" data-testid={`activity-entry-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isFirst ? entryColor.dot : "bg-[#D9D7D0]"}`} />
                        {!isLast && <div className="w-px h-full min-h-[40px] bg-[#D9D7D0]" />}
                      </div>
                      <div className="pb-6">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${isFirst ? "text-[#161616]" : "text-[#5A5955]"}`}>
                            {entry.status}
                          </span>
                        </div>
                        {entry.note && <p className="text-xs text-[#5A5955] mt-0.5">{entry.note}</p>}
                        {entry.assigned_to_name && (
                          <p className="text-xs text-[#00739E] mt-0.5 flex items-center gap-1"><UserCheck size={10} /> Assigned to {entry.assigned_to_name}</p>
                        )}
                        <p className="text-xs text-[#5A5955]/60 mt-1 flex items-center gap-1">
                          <Clock size={10} />{new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quotation */}
            <div className="bg-white border border-[#D9D7D0] p-6" data-testid="order-quotation">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-bold text-[#5A5955]">Approved Quotation</p>
                {order.lead_id && (
                  <Link to={`/admin/leads/${order.lead_id}`} className="text-xs font-bold text-[#00739E] hover:text-[#161616] transition-colors flex items-center gap-1">
                    View all quotations <ExternalLink size={10} />
                  </Link>
                )}
              </div>
              {order.quotation ? (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-[#D9D7D0]">
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955] w-10">#</th>
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955]">Item</th>
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955]">Description</th>
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Cost</th>
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Tax %</th>
                          <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.quotation.items.map((item, ii) => (
                          <tr key={ii} className="border-b border-[#D9D7D0]">
                            <td className="px-4 py-2 text-xs text-[#5A5955]">{ii + 1}</td>
                            <td className="px-4 py-2 text-sm text-[#161616] font-medium">{item.item}</td>
                            <td className="px-4 py-2 text-sm text-[#5A5955]">{item.description || "—"}</td>
                            <td className="px-4 py-2 text-sm text-[#161616] text-right">{parseFloat(item.cost).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-[#161616] text-right">{parseFloat(item.tax).toFixed(2)}%</td>
                            <td className="px-4 py-2 text-sm text-[#161616] font-medium text-right">{parseFloat(item.total).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end mt-3 pt-3 border-t border-[#D9D7D0]">
                    <span className="text-xs font-bold text-[#5A5955] mr-3">Grand Total:</span>
                    <span className="text-lg font-black text-[#161616] tracking-tight">{parseFloat(order.quotation.grand_total).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-[#D9D7D0]">
                  <FileText size={24} className="mx-auto text-[#D9D7D0] mb-2" />
                  <p className="text-sm text-[#5A5955]">No quotation was attached when this order was created.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right column - Customer Info */}
          <div className="space-y-6">
            <div className="bg-white border border-[#D9D7D0] p-6 space-y-4" data-testid="order-customer-info">
              <p className="text-xs font-bold text-[#5A5955]">Customer Information</p>
              <div className="space-y-4">
                <p className="text-lg font-bold text-[#161616]">{order.customer_name}</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#161616] text-white flex items-center justify-center flex-shrink-0"><Mail size={14} /></div>
                  <div>
                    <p className="text-xs font-bold text-[#5A5955]">Email</p>
                    <p className="text-sm text-[#161616]" data-testid="order-email">{order.customer_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#161616] text-white flex items-center justify-center flex-shrink-0"><Phone size={14} /></div>
                  <div>
                    <p className="text-xs font-bold text-[#5A5955]">Phone</p>
                    <p className="text-sm text-[#161616]" data-testid="order-phone">{order.customer_phone || "Not provided"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#161616] text-white flex items-center justify-center flex-shrink-0"><Briefcase size={14} /></div>
                  <div>
                    <p className="text-xs font-bold text-[#5A5955]">Service</p>
                    <p className="text-sm text-[#161616]" data-testid="order-service">{order.service || "Not specified"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#D9D7D0] p-6 space-y-3">
              <p className="text-xs font-bold text-[#5A5955]">Requirements</p>
              <p className="text-sm text-[#161616] whitespace-pre-wrap" data-testid="order-requirements">{order.requirements || "No requirements specified."}</p>
            </div>

            <div className="bg-white border border-[#D9D7D0] p-6 space-y-3">
              <p className="text-xs font-bold text-[#5A5955]">Order Details</p>
              <div className="space-y-2 text-sm text-[#5A5955]">
                <p>Order ID: <span className="font-mono text-xs text-[#161616]">{order.id.slice(0, 8)}...</span></p>
                <p>Created: {new Date(order.created_at).toLocaleString()}</p>
                <p>Updated: {new Date(order.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Selection Modal */}
      {staffModal && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setStaffModal(null)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="staff-assign-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-lg font-bold tracking-tight text-[#161616]">Assign {staffModal.role}</h2>
              <button onClick={() => setStaffModal(null)} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <div className="p-8 space-y-5">
              <p className="text-sm text-[#5A5955]">
                Moving to <span className="font-bold text-[#161616]">{staffModal.status}</span> requires assigning a <span className="font-bold text-[#161616]">{staffModal.role}</span>.
              </p>
              {loadingStaff ? (
                <div className="text-center py-4"><Loader2 className="animate-spin text-[#161616] mx-auto" size={20} /></div>
              ) : staffList.length === 0 ? (
                <div className="text-center py-4 border border-dashed border-[#D9D7D0] px-4">
                  <p className="text-sm text-[#5A5955]">No {staffModal.role} found. Please add one in the Staff page first.</p>
                </div>
              ) : (
                <div>
                  <label className="field-label">Select {staffModal.role}</label>
                  <select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)} data-testid="staff-select-dropdown"
                    className="field">
                    <option value="">Choose a staff member</option>
                    {staffList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStaffModal(null)} className="px-5 py-2.5 text-xs font-bold text-[#5A5955] border border-[#D9D7D0] hover:bg-[#F4F3EF] transition-colors">
                  Cancel
                </button>
                <button onClick={confirmStaffAssignment} disabled={!selectedStaff || updating} data-testid="confirm-staff-assign-btn"
                  className="bg-[#161616] text-white font-bold px-5 py-2.5 text-xs hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200 disabled:opacity-50">
                  {updating ? "Assigning..." : `Assign & Move to ${staffModal.status}`}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
